// Pure date math + recurrence expansion for the calendar grid. No React, no
// external date library — native Date only (consistent with isoToLocalInput in
// entry-form). All placement is in the viewer's LOCAL zone, matching the
// "schedule, not enforced" semantics on web.
import type { CalendarEntry } from "@/api/calendar";

export const WEEKDAY_CODES = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type DayKey = (typeof WEEKDAY_CODES)[number];

/** JS getDay(): 0=Sun..6=Sat → our mon-first code. */
export function dayKeyOf(d: Date): DayKey {
  return WEEKDAY_CODES[(d.getDay() + 6) % 7];
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Local "YYYY-MM-DD" key (NOT toISOString, which is UTC). */
export function ymdKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return ymdKey(a) === ymdKey(b);
}

export function isToday(d: Date): boolean {
  return sameDay(d, new Date());
}

/**
 * The 42 days (6 mon-first weeks) covering `month` — leading/trailing days of
 * adjacent months padded in so the grid height never jumps between months.
 */
export function monthGridDays(month: Date): Date[] {
  const first = startOfMonth(month);
  // Days to back up so the grid starts on a Monday.
  const lead = (first.getDay() + 6) % 7;
  const start = addDays(first, -lead);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** The 7 mon-first days of the week containing `d`. */
export function weekGridDays(d: Date): Date[] {
  const lead = (d.getDay() + 6) % 7;
  const start = addDays(d, -lead);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

export interface Occurrence {
  entry: CalendarEntry;
  /** Within-day ordering: blocks by start time, due items by their local time. */
  sortKey: string;
}

/** Parse an ISO timestamp to its LOCAL ymd key, or null if unparseable. */
function localYmd(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : ymdKey(d);
}

function localHm(iso: string | null): string {
  if (!iso) return "99:99";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "99:99";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Expand entries into a per-day map over `visibleDays`:
 *   - block with days[] → on each visible day whose weekday is in the set
 *   - block one-off (on_date) → on that local date
 *   - homework/task with due_at → on its LOCAL due day
 * Entries with no placeable date are returned separately via `unscheduled`.
 */
export function buildOccurrences(
  entries: CalendarEntry[],
  visibleDays: Date[],
): { byDay: Map<string, Occurrence[]>; unscheduled: CalendarEntry[] } {
  const visible = new Set(visibleDays.map(ymdKey));
  const byDay = new Map<string, Occurrence[]>();
  const unscheduled: CalendarEntry[] = [];

  const push = (key: string, occ: Occurrence) => {
    const arr = byDay.get(key);
    if (arr) arr.push(occ);
    else byDay.set(key, [occ]);
  };

  for (const day of visibleDays) {
    const key = ymdKey(day);
    const code = dayKeyOf(day);
    for (const e of entries) {
      if (e.entry_kind === "block" && e.days.length > 0 && e.days.includes(code)) {
        push(key, { entry: e, sortKey: e.start_time ?? "00:00" });
      }
    }
  }

  for (const e of entries) {
    if (e.entry_kind === "block") {
      if (e.days.length === 0 && e.on_date && visible.has(e.on_date)) {
        push(e.on_date, { entry: e, sortKey: e.start_time ?? "00:00" });
      }
      continue;
    }
    // homework / task
    const dueKey = localYmd(e.due_at);
    if (dueKey && visible.has(dueKey)) {
      push(dueKey, { entry: e, sortKey: `z${localHm(e.due_at)}` });
    } else if (!dueKey) {
      unscheduled.push(e);
    }
  }

  for (const arr of byDay.values()) arr.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return { byDay, unscheduled };
}
